import { Injectable } from '@angular/core';
import {
  Actions,
  Effect,
  ofType
} from '@ngrx/effects';
import {
  Action,
  select,
  Store
} from '@ngrx/store';
import {
  asyncScheduler,
  defer,
  Observable,
  of
} from 'rxjs';
import {
  buffer,
  catchError,
  debounceTime,
  filter,
  map,
  mapTo,
  mergeMap,
  switchMap,
  withLatestFrom
} from 'rxjs/operators';
import { v4 as UUID } from 'uuid';

import { SchuecoProductValidationResponseInterface } from '@p1/libs/schueco-product-validation/src/lib/model/schueco-product-validation-response.interface';
import { NotificationType } from '@p1/libs/ui-elements';

import * as keycloakActions from '../../auth/action/keycloak.actions';
import * as notificationActions from '../../core/actions/notification.actions';
import * as productActions from '../../product/actions/product.actions';
import * as productComparisonActions from '../../product-comparison/actions/product-comparison.actions';
import * as productComparisonReducer from '../../product-comparison/reducer/product-comparison.reducer';
import * as supplierActions from '../../supplier/actions/supplier.actions';
import * as productValidationActions from '../../product-validation/actions/product-validation.actions';
import * as routerActions from '../../routing/actions/router.actions';
import { GoAction } from '../../routing/actions/router.actions';
import { WatchlistElementObjectType } from '../../watchlists/model/watchlist-element/watchlist-element-object-type.enum';
import * as myWatchlistsActions from '../actions/my-watchlists.actions';
import * as myWatchlistsReducer from '../reducer';
import { MyWatchlistsService } from '../service/my-watchlists.service';
import * as productReducer from '../../product/reducer';
import * as productValidationReducer from '../../product-validation/reducer';
import * as supplierReducer from '../../supplier/reducer';
import * as authReducer from '../../auth/reducer/auth.reducer';
import { uniqueArray } from '../../shared/common/helper/unique-array/unique-array';
import { Product } from '../../product/model';
import { Supplier } from '../../supplier/model';


@Injectable()
/**
 * Contains side effects for my watchlists actions
 */
export class MyWatchlistsEffects {


  @Effect()
  /**
   * creates a personal watchlist
   * Dispatches either succeeded or failed action afterwards
   */
  createMyWatchlist$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.CREATE_MY_WATCHLIST),
    map(action => action.payload),
    switchMap(payload => this._myWatchlistsService.createMyWatchlist(payload.watchlistFormData).pipe(
      map(watchlist => new myWatchlistsActions.CreateMyWatchlistSucceededAction({
        watchlist,
        message: payload.succeededMessage
      })),
      catchError(error => of(new myWatchlistsActions.CreateMyWatchlistFailedAction({
        error,
        message: payload.succeededMessage
      })))
    ))
  );

  @Effect()
  /**
   * dispatches a success notification for a created watchlist
   */
  createMyWatchlistSucceeded$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.CREATE_MY_WATCHLIST_SUCCEEDED),
    map(action => action.payload),
    map(payload => new notificationActions.AddNotificationAction({
      uuid: UUID(),
      content: payload.message ||
               $localize`:|create watchlist succeeded content @@my-watchlist.effects_create-succeeded-content:The watchlist {{watchlistName}} has been created.`
                 .replace('{{watchlistName}}', payload.watchlist.name.length > 60 ? payload.watchlist.name.slice(0, 60) + '...' : payload.watchlist.name),
      type: NotificationType.SUCCESS,
      useTimeout: true
    }))
  );

  @Effect()
  /**
   * dispatches a creation failed notification for a watchlist
   */
  createMyWatchlistFailed$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.CREATE_MY_WATCHLIST_FAILED),
    map(() => new notificationActions.AddNotificationAction({
      uuid: UUID(),
      content: $localize`:|create watchlist failed content @@my-watchlist.effects_create-failed-content:The watchlist could not be created.`,
      type: NotificationType.ERROR,
      useTimeout: false
    }))
  );

  @Effect()
  /**
   * patch a personal watchlist
   */
  patchMyWatchlist$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.PATCH_MY_WATCHLIST),
    map(action => action.payload),
    mergeMap(payload => this._myWatchlistsService.patchMyWatchlist(payload.uuid, payload.watchlistData).pipe(
      map(myWatchlist => new myWatchlistsActions.PatchMyWatchlistSucceededAction(myWatchlist)),
      catchError(error => of(new myWatchlistsActions.PatchMyWatchlistFailedAction(error)))
    ))
  );

  @Effect()
  /**
   * dispatches a success notification for a patched watchlist
   */
  patchMyWatchlistSucceeded$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.PATCH_MY_WATCHLIST_SUCCEEDED),
    map(action => action.payload),
    map(payload => new notificationActions.AddNotificationAction({
      uuid: UUID(),
      content: $localize`:|update watchlist succeeded content @@my-watchlist.effects_update-succeeded-content:The watchlist {{watchlistName}} has been updated.`
        .replace('{{watchlistName}}', payload.name.length > 60 ? payload.name.slice(0, 60) + '...' : payload.name),
      type: NotificationType.SUCCESS,
      useTimeout: true
    }))
  );

  @Effect()
  /**
   * dispatches a patch failed notification for a watchlist
   */
  patchMyWatchlistFailed$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.PATCH_MY_WATCHLIST_FAILED),
    map(() => new notificationActions.AddNotificationAction({
      uuid: UUID(),
      content: $localize`:|update watchlist failed content @@my-watchlist.effects_update-failed-content:The watchlist could not be updated.`,
      type: NotificationType.ERROR,
      useTimeout: false
    }))
  );

  @Effect()
  /**
   * fetches the summary of all personal watchlists
   * Dispatches either succeeded or failed action afterwards
   */
  fetchMyWatchlist$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.FETCH_MY_WATCHLISTS),
    switchMap(() => this._myWatchlistsService.getMyWatchlists().pipe(
      map(myWatchlists => new myWatchlistsActions.FetchMyWatchlistsSucceededAction(myWatchlists)),
      catchError(error => of(new myWatchlistsActions.FetchMyWatchlistsFailedAction(error)))
    ))
  );

  @Effect()
  /**
   * fetches the first level of personal watchlists
   * Dispatches either succeeded or failed action afterwards
   */
  fetchMyWatchlistFirstLevel$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.FETCH_MY_WATCHLISTS_FIRST_LEVEL),
    switchMap(() => this._myWatchlistsService.getMyWatchlistsFirstLevel().pipe(
      map(myWatchlists => new myWatchlistsActions.FetchMyWatchlistsFirstLevelSucceededAction(myWatchlists)),
      catchError(error => of(new myWatchlistsActions.FetchMyWatchlistsFirstLevelFailedAction(error)))
    ))
  );

  @Effect()
  /**
   * fetches the detail info for a personal watchlist
   * Dispatches either succeeded or failed action afterwards
   */
  fetchMyWatchlistDetail$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.FETCH_MY_WATCHLIST_DETAIL),
    map(action => action.payload),
    mergeMap(payload => this._myWatchlistsService.fetchMyWatchlistDetail(payload.uuid).pipe(
      map(myWatchlist => new myWatchlistsActions.FetchMyWatchlistDetailSucceededAction(myWatchlist)),
      catchError(error => of(new myWatchlistsActions.FetchMyWatchlistDetailFailedAction(error)))
    ))
  );

  /**
   * load details for a selected watchlist
   */
  @Effect()
  selectMyWatchlist$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.SELECT_MY_WATCHLIST),
    map(action => action.payload),
    filter(payload => payload.uuid !== undefined),
    map(payload => new myWatchlistsActions.FetchMyWatchlistDetailAction({ uuid: payload.uuid }))
  );

  @Effect()
  /**
   * removes a single personal list
   * Dispatches either succeeded or failed action afterwards
   */
  removeMyWatchlist$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.REMOVE_MY_WATCHLIST),
    map(action => action.payload),
    withLatestFrom(
      this._store.pipe(
        select(myWatchlistsReducer.getMyWatchlists)
      ),
      this._store.pipe(
        select(myWatchlistsReducer.getSelectedWatchlistUuid)
      )
    ),
    switchMap(([payload, watchlists, selectedUuid]) => this._myWatchlistsService.removeMyWatchlist(payload.uuid).pipe(
      mapTo(new myWatchlistsActions.RemoveMyWatchlistSucceededAction({
        uuid: payload.uuid,
        watchlist: watchlists[payload.uuid],
        wasSelected: selectedUuid === payload.uuid
      })),
      catchError(error => of(new myWatchlistsActions.RemoveMyWatchlistFailedAction(error)))
    ))
  );

  @Effect()
  /**
   * dispatches a success notification on removal of a watchlist element
   */
  removeMyWatchlistSucceeded$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.REMOVE_MY_WATCHLIST_SUCCEEDED),
    map(action => action.payload),
    mergeMap(payload => {
      const actions: Action[] = [
        new notificationActions.AddNotificationAction({
          uuid: UUID(),
          content: $localize`:|remove watchlist succeeded content @@my-watchlist.effects_remove-succeeded-content:The watchlist {{watchlistName}} has been removed.`
            .replace(
              '{{watchlistName}}',
              payload.watchlist ? (payload.watchlist.name.length > 60 ? payload.watchlist.name.slice(0, 60) + '...' : payload.watchlist.name) : ''
            ),
          type: NotificationType.SUCCESS,
          useTimeout: true
        })
      ];

      if (payload.wasSelected && payload.watchlist) {
        // when deleting the currently viewed watchlist, we navigate to its parent or the overview
        if (payload.watchlist.parentUuid) {
          actions.push(new GoAction({
            path: ['/myWatchlists', payload.watchlist.parentUuid]
          }));
        } else {
          actions.push(new GoAction({
            path: ['/myWatchlists']
          }));
        }
      }

      return actions;
    })
  );

  @Effect()
  /**
   * dispatches a fail notification on removal of a watchlist element
   */
  removeMyWatchlistFailed$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.REMOVE_MY_WATCHLIST_FAILED),
    map(action => action.payload),
    map(() => new notificationActions.AddNotificationAction({
      uuid: UUID(),
      content: $localize`:|remove watchlist failed content @@my-watchlist.effects_remove-failed-content:The watchlist could not be removed.`,
      type: NotificationType.ERROR,
      useTimeout: false
    }))
  );

  @Effect()
  /**
   * removes a single item from a personal list
   * Dispatches either succeeded or failed action afterwards
   */
  removeElementFromMyWatchlist$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.REMOVE_ELEMENT_FROM_MY_WATCHLIST),
    map(action => action.payload),
    switchMap(payload => this._myWatchlistsService.removeElementFromMyWatchlist(payload.watchlistUuid, payload.elementUuid).pipe(
      mapTo(new myWatchlistsActions.RemoveElementFromMyWatchlistSucceededAction(payload)),
      catchError(error => of(new myWatchlistsActions.RemoveElementFromMyWatchlistFailedAction(error)))
    ))
  );

  @Effect()
  /**
   * dispatches a success notification on removal of a watchlist element
   */
  removeElementFromMyWatchlistSucceeded$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.REMOVE_ELEMENT_FROM_MY_WATCHLIST_SUCCEEDED),
    map(action => action.payload),
    map(() => new notificationActions.AddNotificationAction({
      uuid: UUID(),
      content: $localize`:|remove watchlist item succeeded content @@my-watchlist.effects_remove-item-succeeded-content:The element has been removed from the watchlist.`,
      type: NotificationType.SUCCESS,
      useTimeout: true
    }))
  );

  @Effect()
  /**
   * dispatches a success notification on removal of a watchlist element
   */
  removeElementFromMyWatchlistFailed$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.REMOVE_ELEMENT_FROM_MY_WATCHLIST_FAILED),
    map(action => action.payload),
    map(() => new notificationActions.AddNotificationAction({
      uuid: UUID(),
      content: $localize`:|remove watchlist item failed content @@my-watchlist.effects_remove-item-failed-content:The element could not be removed from the watchlist.`,
      type: NotificationType.ERROR,
      useTimeout: false
    }))
  );


  @Effect()
  /**
   * patch an element comment on watchlist
   */
  patchMyWatchlistElementComment$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.COMMENT_ELEMENT_ON_MY_WATCHLIST),
    map(action => action.payload),
    mergeMap(payload => this._myWatchlistsService.commentMyWatchlistPreviewElement(payload.watchlistUuid, payload.elementUuid, payload.comment).pipe(
      map(() => new myWatchlistsActions.CommentElementOnMyWatchlistSucceededAction(payload)),
      catchError(error => of(new myWatchlistsActions.CommentElementOnMyWatchlistFailedAction(error)))
    ))
  );

  @Effect()
  /**
   * dispatches a success notification on comment of a watchlist element
   */
  patchMyWatchlistElementCommentSucceeded$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.COMMENT_ELEMENT_ON_MY_WATCHLIST_SUCCEEDED),
    mapTo(new notificationActions.AddNotificationAction({
      content: $localize`:|comment watchlist item succeeded content @@my-watchlist.effects_comment-item-succeeded-content:Your comment has been saved.`,
      uuid: UUID(),
      type: NotificationType.SUCCESS,
      useTimeout: true
    }))
  );

  @Effect()
  /**
   * dispatches a error notification on failed comment of a watchlist element
   */
  patchMyWatchlistElementCommentFailed$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.COMMENT_ELEMENT_ON_MY_WATCHLIST_FAILED),
    mapTo(new notificationActions.AddNotificationAction({
      content: $localize`:|comment watchlist item failed content @@my-watchlist.effects_comment-item-failed-content:Your comment could not be saved.`,
      uuid: UUID(),
      type: NotificationType.ERROR,
      useTimeout: true
    }))
  );

  @Effect()
  addElementToMyWatchlist$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.ADD_ELEMENT_TO_MY_WATCHLIST),
    map(action => action.payload),
    withLatestFrom(
      this._store.pipe(
        select(productValidationReducer.getProductValidationResult),
        map(result => result?.productUuid)
      )
    ),
    mergeMap(([payload, productUuid]) => {
      const elementForm = productUuid && payload.elementObjectType === 'ProductValidation' ?
          {...payload.elementForm, additionalProperties: {productUuid}} : payload.elementForm;
      return this._myWatchlistsService.addElementToMyWatchlist(
        payload.watchlistUuid, elementForm
      ).pipe(
        mapTo(new myWatchlistsActions.AddElementToMyWatchlistSucceededAction(payload)),
        catchError(err => of(new myWatchlistsActions.AddElementToMyWatchlistFailedAction(err)))
      );
    })
  );

  @Effect()
  addElementToMyWatchlistSucceeded$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.ADD_ELEMENT_TO_MY_WATCHLIST_SUCCEEDED),
    map(action => action.payload),
    withLatestFrom(this._store.pipe(
      select(myWatchlistsReducer.getMyWatchlists)
    )),
    map(([payload, watchlists]) => {
      let content;
      const watchlistName = watchlists[payload.watchlistUuid].name;

      switch (payload.elementObjectType) {
        case WatchlistElementObjectType.Product:
          content = $localize`:|add watchlist product succeeded content @@my-watchlist.effects_add-product-succeeded-content:The product has been added to the watchlist <b>{{watchlistName}}</b>.`
            .replace('{{watchlistName}}', watchlistName.length > 60 ? watchlistName.slice(0, 60) + '...' : watchlistName);
          break;
        default:
          content = $localize`:|add watchlist item succeeded content @@my-watchlist.effects_add-item-succeeded-content:The element has been added to the watchlist <b>{{watchlistName}}</b>.`
            .replace('{{watchlistName}}', watchlistName.length > 60 ? watchlistName.slice(0, 60) + '...' : watchlistName);
      }

      return new notificationActions.AddNotificationAction({
        content,
        uuid: UUID(),
        type: NotificationType.SUCCESS,
        useTimeout: true,
        actions: [
          {
            label: $localize`:|add watchlist item succeeded label @@my-watchlist.effects_add-item-succeeded-label:Show watchlist`,
            action: new routerActions.GoAction({ path: ['myWatchlists', payload.watchlistUuid] })
          }
        ]
      });
    })
  );

  @Effect()
  addElementToMyWatchlistFailed$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.ADD_ELEMENT_TO_MY_WATCHLIST_FAILED),
    mapTo(new notificationActions.AddNotificationAction({
      content: $localize`:|add watchlist item failed content @@my-watchlist.effects_add-item-failed-content:The element could not be added to the watchlist.`,
      uuid: UUID(),
      type: NotificationType.ERROR,
      useTimeout: false
    }))
  );

  /**
   * Creates a snapshot for the watchlist with the given uuid
   *
   * @type {Observable<any>}
   */
  @Effect()
  createWatchlistSnapshot$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.CREATE_WATCHLIST_SNAPSHOT),
    map(action => action.payload),
    switchMap(({ watchlistUuid }) => this._myWatchlistsService.createWatchlistSnapshot(watchlistUuid).pipe(
      map((snapshotUuid) => new myWatchlistsActions.CreateWatchlistSnapshotSucceededAction({
        watchlistUuid,
        snapshotUuid
      })),
      catchError(error => of(new myWatchlistsActions.CreateWatchlistSnapshotFailedAction(error)))
    ))
  );

  @Effect()
  updateAccessToken$: Observable<Action> = this._actions.pipe(
    ofType(keycloakActions.KEYCLOAK_LOGIN_SUCCEEDED),
    mapTo(new myWatchlistsActions.FetchMyWatchlistsAction())
  );

  @Effect()
  /**
   * fetches element watchlist mapping
   * Dispatches either succeeded or failed action afterwards
   */
  renewElementWatchlistMapping$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.RENEW_ELEMENT_WATCHLIST_MAPPING),
    withLatestFrom(
      this._store.pipe(select(productReducer.getSearchResults), catchError(() => of(undefined))),
      this._store.pipe(select(productReducer.getItems), catchError(() => of(undefined))),
      this._store.pipe(select(supplierReducer.getSearchResults), catchError(() => of(undefined))),
      this._store.pipe(select(supplierReducer.getItems), catchError(() => of(undefined))),
      this._store.pipe(select(productComparisonReducer.getComparisonUuid), catchError(() => of(undefined))),
      this._store.pipe(select(productReducer.getQueryUuid), catchError(() => of(undefined))),
      this._store.pipe(select(productValidationReducer.getProductValidationResult), catchError(() => of(undefined)))
    ),
    map(([
      _, productSearchResults, productItems, supplierSearchResults, supplierItems,
      comparisonUuid, queryUuid, validationResultItem
    ]: [any, Product[], Product[], Supplier[], Supplier[], string, string, SchuecoProductValidationResponseInterface]) => {
      const elementUuids: Set<string> = new Set<string>();

      if (Array.isArray(productSearchResults)) {
        productSearchResults.forEach(product => elementUuids.add(product.uuid));
      }

      if (Array.isArray(productItems)) {
        productItems.forEach(product => elementUuids.add(product.uuid));
      }

      if (Array.isArray(supplierSearchResults)) {
        supplierSearchResults.forEach(supplier => elementUuids.add(supplier.uuid));
      }

      if (Array.isArray(supplierItems)) {
        supplierItems.forEach(supplier => {
          elementUuids.add(supplier.uuid);
          const productsOfSupplier = this.getProductUuidsFromSupplier(supplier);
          productsOfSupplier.forEach(product => elementUuids.add(product));
        });
      }

      if (comparisonUuid) {
        elementUuids.add(comparisonUuid);
      }

      if (queryUuid) {
        elementUuids.add(queryUuid);
      }

      if (validationResultItem) {
        elementUuids.add(validationResultItem.validationUuid);
      }

      return new myWatchlistsActions.FetchElementWatchlistMappingAction(Array.from(elementUuids));
    })
  );

  @Effect()
  elementWatchlistMappingAfterRemoveWatchlist$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.REMOVE_MY_WATCHLIST_SUCCEEDED),
    mapTo(new myWatchlistsActions.RenewElementWatchlistMappingAction())
  );

  @Effect()
  elementWatchlistMappingAfterCreateWatchlistWithElements$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.CREATE_MY_WATCHLIST_SUCCEEDED),
    map(action => action.payload),
    filter(action => !!action.watchlist?.elements),
    map(() => new myWatchlistsActions.RenewElementWatchlistMappingAction())
  );

  @Effect()
  elementWatchlistMappingAfterProductMulti$: Observable<Action> = this._actions.pipe(
    ofType(productActions.FETCH_MULTI_SUCCEEDED),
    map(action => action.payload),
    filter(payload => Array.isArray(payload.items)),
    map((payload) => {
      const elementUuids = payload.items.map(item => item.uuid);
      return new myWatchlistsActions.FetchElementWatchlistMappingAction(elementUuids);
    })
  );

  @Effect()
  elementWatchlistMappingAfterProductSingle$: Observable<Action> = this._actions.pipe(
    ofType(productActions.FETCH_SINGLE_SUCCEEDED),
    map(action => new myWatchlistsActions.FetchElementWatchlistMappingAction([action.payload.uuid]))
  );

  @Effect()
  elementWatchlistMappingAfterProductComparison$: Observable<Action> = this._actions.pipe(
    ofType(productComparisonActions.loadProductComparisonSuccess),
    map(action => action.productComparison),
    filter(productComparison => !!productComparison.productUuids),
    map((productComparison) => new myWatchlistsActions.FetchElementWatchlistMappingAction(productComparison.productUuids))
  );

  @Effect()
  elementWatchlistMappingAfterSupplierMulti$: Observable<Action> = this._actions.pipe(
    ofType(supplierActions.FETCH_MULTI_SUCCEEDED),
    map(action => action.payload),
    filter(payload => Array.isArray(payload.items)),
    map((payload) => {
      const elementUuids = payload.items.map(item => item.uuid);
      return new myWatchlistsActions.FetchElementWatchlistMappingAction(elementUuids);
    })
  );

  @Effect()
  elementWatchlistMappingAfterSupplierSingle$: Observable<Action> = this._actions.pipe(
    ofType(supplierActions.FETCH_SINGLE_SUCCEEDED),
    map(action => new myWatchlistsActions.FetchElementWatchlistMappingAction([action.payload.uuid]))
  );

  @Effect()
  elementWatchlistMappingAfterSupplierSingleSummary$: Observable<Action> = this._actions.pipe(
    ofType(supplierActions.FETCH_SINGLE_SUMMARY_SUCCEEDED),
    map(action => new myWatchlistsActions.FetchElementWatchlistMappingAction([action.payload.uuid]))
  );

  @Effect()
  elementWatchlistMappingAfterWatchlistsFetched$: Observable<Action> = this._actions.pipe(
    ofType(myWatchlistsActions.FETCH_MY_WATCHLISTS_SUCCEEDED),
    map(() => new myWatchlistsActions.RenewElementWatchlistMappingAction())
  );

  @Effect()
  elementWatchlistMappingAfterValidationFetched$: Observable<Action> = this._actions.pipe(
    ofType(productValidationActions.fetchProductValidationTriggeredSuccess),
    map(action => action.validationUuid),
    map(validationUuid => new myWatchlistsActions.FetchElementWatchlistMappingAction([validationUuid]))
  );

  @Effect()
  elementWatchlistMappingAfterSimilarProducts$: Observable<Action> = this._actions.pipe(
    ofType(productActions.FETCH_SIMILAR_PRODUCTS_SUCCEEDED),
    map(action => action.payload.similarProducts),
    map((payload) => {
      const elementUuids = payload.map(item => item.uuid);
      return new myWatchlistsActions.FetchElementWatchlistMappingAction(elementUuids);
    })
  );

  @Effect()
  init$: Observable<Action> = defer(() => of(new myWatchlistsActions.FetchMyWatchlistsAction()));


  /**
   * @param _store
   * @param _actions
   * @param _myWatchlistsService
   */
  constructor(
    private _store: Store<myWatchlistsReducer.State>,
    private _actions: Actions<myWatchlistsActions.Actions | productActions.Actions | supplierActions.Actions | productValidationActions.Actions>,
    private _myWatchlistsService: MyWatchlistsService
  ) {}

  @Effect()
  /**
   * fetches element watchlist mapping
   * buffered for only calling the api once a second
   * dispatches either succeeded or failed action afterwards
   */
  fetchElementWatchlistMapping$ = ({ debounce = 1000, scheduler = asyncScheduler } = {}) => this._actions.pipe(
    ofType(myWatchlistsActions.FETCH_ELEMENT_WATCHLIST_MAPPING),
    map(action => action.payload),
    buffer(this._actions.pipe(
      ofType(myWatchlistsActions.FETCH_ELEMENT_WATCHLIST_MAPPING),
      debounceTime(debounce, scheduler)
    )),
    withLatestFrom(this._store.pipe(select(authReducer.getIsAuthenticated))),
    filter(([uuidLists, isAuthenticated]) => !!isAuthenticated && uuidLists.length > 0),
    map(([uuidLists, _]) => uniqueArray<string>([].concat(...uuidLists))),
    filter(payload => payload.length > 0),
    switchMap((payload) => this._myWatchlistsService.fetchElementsWatchlistMapping(payload).pipe(
      map(elementWatchlistMapping => new myWatchlistsActions.FetchElementWatchlistMappingSucceededAction(elementWatchlistMapping)),
      catchError(error => of(new myWatchlistsActions.FetchElementWatchlistMappingFailedAction(error)))
    ))
  );

  private getProductUuidsFromSupplier(supplier: Supplier) {
    const productUuids = [];
    if (Array.isArray(supplier.supplierCategories)) {
      supplier.supplierCategories.forEach(cat => {
        if (Array.isArray(cat.products)) {
          cat.products.forEach(supplierProduct => {
            productUuids.push(supplierProduct.uuid);
          });
        }
      });
    }

    if (Array.isArray(supplier.supplierBimProducts)) {
      supplier.supplierBimProducts.forEach(product => productUuids.push(product.uuid));
    }

    return productUuids;
  }
}
